import { Action, Subject } from '@erp/authz';
import { useCan } from '@/hooks/useCan';
import {
  useAgentPageStatuses,
  useAgentPages,
} from '@/api/rpc/hooks/agent-pages/useAgentPages';
import { taskPageNavIcon } from '@/components/task-pages/taskPageIcons';
import { AgentPageStatusBadge } from '@/components/sidenav/AgentPageStatusBadge';
import {
  agentPageNavEntry,
  type AgentPageNavItem,
} from '@/routing/agent-pages-nav';

/**
 * The current user's visible agent pages as sidebar nav items. Empty while
 * loading, when the org has no pages, or when the user lacks Read on
 * BackgroundAgent (the query is never fired then). Audience membership is
 * enforced by the list endpoint. The agent monitor flag does not apply:
 * a page assigned to a team is part of that team's navigation.
 *
 * Each item carries a marker for the agent's latest run (running / failed)
 * once the statuses query resolves.
 */
export function useAgentPageNavItems(): AgentPageNavItem[] {
  const [canReadAgents] = useCan(Action.Read, Subject.BackgroundAgent);
  const enabled = canReadAgents;
  const { data: agentPages } = useAgentPages({ enabled });
  const { data: statusesData } = useAgentPageStatuses({
    enabled: enabled && (agentPages?.length ?? 0) > 0,
  });

  if (!enabled || !agentPages) return [];
  return agentPages.map((page) => {
    const status = statusesData?.statuses[page.id];
    return {
      entry: agentPageNavEntry({
        page,
        icon: taskPageNavIcon(page.icon),
        badge: status ? <AgentPageStatusBadge status={status} /> : null,
      }),
      navGroup: page.navGroup,
      pageSectionId: page.pageSectionId,
      sortOrder: page.sortOrder,
    };
  });
}
